import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
import { Icon } from '@/shared/components/Icon';
import { cn } from '@/shared/helpers/cn';
import styles from './Pagination.module.scss';

export const PAGE_SIZE_OPTIONS = [25, 50, 100] as const;
export type PageSize = (typeof PAGE_SIZE_OPTIONS)[number];

export interface PaginationProps {
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  /** Nome do que esta sendo listado, para a contagem ("137 pacientes"). */
  itemLabel?: string;
  className?: string;
}

/**
 * Paginacao: primeira, anterior, proxima, ultima — igual ao sistema legado.
 *
 * A contagem de resultados fica numa regiao `aria-live`, para quem usa leitor de
 * tela perceber que a lista mudou sem precisar varrer a tabela de novo.
 */
export function Pagination({
  page,
  pageSize,
  total,
  totalPages,
  onPageChange,
  itemLabel = 'registros',
  className,
}: PaginationProps) {
  const first = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const last = Math.min(page * pageSize, total);
  const atStart = page <= 1;
  const atEnd = page >= totalPages;

  return (
    <nav className={cn(styles.root, className)} aria-label="Paginação">
      <div className={styles.buttons}>
        <button
          type="button"
          className={styles.button}
          onClick={() => onPageChange(1)}
          disabled={atStart}
          aria-label="Primeira página"
        >
          <Icon as={ChevronsLeft} size={16} />
        </button>
        <button
          type="button"
          className={styles.button}
          onClick={() => onPageChange(page - 1)}
          disabled={atStart}
          aria-label="Página anterior"
        >
          <Icon as={ChevronLeft} size={16} />
        </button>

        <div className={styles.info}>
          <span className={styles.page}>
            Página {page} de {Math.max(totalPages, 1)}
          </span>
          <span className={styles.count} aria-live="polite">
            {total === 0 ? `Nenhum de ${total} ${itemLabel}` : `${first}–${last} de ${total} ${itemLabel}`}
          </span>
        </div>

        <button
          type="button"
          className={styles.button}
          onClick={() => onPageChange(page + 1)}
          disabled={atEnd}
          aria-label="Próxima página"
        >
          <Icon as={ChevronRight} size={16} />
        </button>
        <button
          type="button"
          className={styles.button}
          onClick={() => onPageChange(totalPages)}
          disabled={atEnd}
          aria-label="Última página"
        >
          <Icon as={ChevronsRight} size={16} />
        </button>
      </div>
    </nav>
  );
}

export interface PageSizeSelectProps {
  value: number;
  onChange: (pageSize: number) => void;
  className?: string;
}

/** Seletor de itens por pagina — 25, 50 ou 100, como no sistema legado. */
export function PageSizeSelect({ value, onChange, className }: PageSizeSelectProps) {
  return (
    <label className={cn(styles.pageSize, className)}>
      <span className={styles.pageSizeLabel}>Itens por página</span>
      <select
        className={styles.pageSizeSelect}
        value={value}
        onChange={(event) => onChange(Number(event.target.value))}
      >
        {PAGE_SIZE_OPTIONS.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
    </label>
  );
}
